/**
 * SZEREPKÖR-DÖNTÉSEK EGY HELYEN — a GÉPI névből, a megjelenített névre ESVE VISSZA.
 * [B-W4, 2026-09-16]
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * MI VOLT A BAJ
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * A szerver KÉT különböző nevet tart ugyanarról a szerepkörről:
 *   · `UserRole.Name`        — GÉPI, változatlan: `Coach`, `Player`, `Administrator`;
 *   · `UserRole.DisplayName` — MEGJELENÍTETT, magyar: „Edző", „Játékos", „Adminisztrátor" —
 *                              és ezt az admin a szerepkör-kezelésben ÁTNEVEZHETI.
 *
 * A webes felület négy helyen a MEGJELENÍTETT névre épített GÉPI döntést:
 *   · `EventAttendance.tsx:515`  `isCoachRole = roleName === 'Edző'` — ettől függött a sor
 *     címkéje („beszedett" vs. „fizetett") ÉS a saját-edző felismerése;
 *   · `Calendar/EventDetailsDialog.tsx` — „edző vagyok-e ezen az eseményen";
 *   · `UserProfile.tsx`, `UserProfileDialog.tsx` — mely szerepkör-függő mezők látszanak
 *     (óradíj + bankszámla vs. magánóra-díj).
 * Ha az admin a „Edző" display-nevet átírja (pl. „Tréner"), MINDEZ megbicsaklik: az edző sora
 * játékosként viselkedik, az óradíj-mező eltűnik, a jelenléti ív jogosultsága elcsúszik. A
 * felület tehát egy SZABADON SZERKESZTHETŐ szövegre épített jogosultsági szabályt.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * A JAVÍTÁS
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * A szerver ADDITÍVAN adja a gépi nevet is (`UserRoleSystemName` — `EventParticipantResponseDto`
 * és `UserProfileDto`), és a döntés innen megy. A megjelenített név MARAD tartaléknak:
 *
 *  ⚠️ MIÉRT KELL A TARTALÉK, ÉS MIÉRT NEM „PIROSRA VÁLTÁS": a régi mobil APK és a régebbi
 *  válasz-alakok (illetve a tesztfixture-ök) nem hordozzák a gépi nevet. Ha a gépi név
 *  hiányában egyszerűen `false`-t adnánk, egy hiányzó mező NÉMÁN elvenné az edző jogosultságát —
 *  vagyis a javítás rosszabb lenne a hibánál. Így a viselkedés a régi alakon VÁLTOZATLAN, az
 *  újon pedig átnevezés-biztos.
 *
 * A MOBIL párja: `mobile/utils/roles.ts`. ⚠️ ITT KORÁBBAN AZ ÁLLT, hogy „a mobil ezen a leleten
 * nem érintett, mert ott a két alak összevetése eddig is egy helyen volt". Ez FÉLREVEZETŐ volt:
 * a mobil valóban EGY helyen döntött (`isCoachRoleName`), de UGYANARRA a szabadon szerkeszthető
 * display-névre — tehát ugyanaz a hiba volt, csak egy helyre összefogva. Az egy hely a JAVÍTÁST
 * könnyítette meg, nem a hibát szüntette meg. A mobil 2026-09-16 óta szintén a gépi névből dönt
 * (`mobile/utils/roles.ts` `isCoachRole`, ugyanez a sorrend és ugyanez a tartalék-szabály);
 * érintett volt a jelenléti ív „BESZED"/„FIZET" címkéje, az edzők-előre rendezés, a részleges
 * jelenlét pill (az edző KIFIZETÉSE) és a részletező `isCoach` jelzője [B-M5].
 */

/** A GÉPI szerepkör-nevek, ahogy a szerver `UserRole.Name`-ben tárolja őket. */
export const ROLE_SYSTEM_NAMES = {
  administrator: 'Administrator',
  coach: 'Coach',
  player: 'Player',
} as const;

/**
 * A magyar, MEGJELENÍTETT nevek MAI értékei — kizárólag TARTALÉKKÉNT, ha a gépi név nem jött meg.
 * ⚠️ Új szabályt SOHA ne erre írj: ezek a szerver adatbázisában szerkeszthető szövegek.
 */
const DISPLAY_FALLBACK: Record<'coach' | 'player' | 'administrator', readonly string[]> = {
  coach: ['edző', 'edzo'],
  player: ['játékos', 'jatekos'],
  administrator: ['adminisztrátor', 'adminisztrator'],
};

const norm = (value?: string | null): string => (value ?? '').trim().toLowerCase();

/**
 * Ez a szerepkör az adott gépi szerepkör-e?
 *
 * @param systemName  a szerver `UserRoleSystemName`-je (`Coach` / `Player` / `Administrator`)
 * @param displayName a megjelenített név — CSAK akkor számít, ha a gépi név nem jött meg
 */
function matches(
  which: 'coach' | 'player' | 'administrator',
  systemName?: string | null,
  displayName?: string | null
): boolean {
  const system = norm(systemName);
  // ⚠️ A GÉPI NÉV ELSŐBBSÉGE ABSZOLÚT: ha megjött, a megjelenített nevet MEG SEM NÉZZÜK.
  // Enélkül egy átnevezett display-név „visszahozhatná" a hibát azon az úton, ahol a gépi
  // név épp mást mond.
  if (system) return system === norm(ROLE_SYSTEM_NAMES[which]);

  const display = norm(displayName);
  if (!display) return false;
  // Tartalék: a magyar címke VAGY maga a gépi név (a régi válaszok közül van, amelyik a
  // `UserRoleName`-be is a gépi nevet tette — pl. `PlannedEventsController`).
  return display === norm(ROLE_SYSTEM_NAMES[which]) || DISPLAY_FALLBACK[which].includes(display);
}

/** Edző-e ez a szerepkör? */
export const isCoachRole = (systemName?: string | null, displayName?: string | null): boolean =>
  matches('coach', systemName, displayName);

/** Játékos-e ez a szerepkör? */
export const isPlayerRole = (systemName?: string | null, displayName?: string | null): boolean =>
  matches('player', systemName, displayName);

/** Adminisztrátor-e ez a szerepkör? */
export const isAdministratorRole = (
  systemName?: string | null,
  displayName?: string | null
): boolean => matches('administrator', systemName, displayName);
